// ============================================================
//  Client API — Bearer token en localStorage
// ============================================================

import type {
  Balise,
  Groupe,
  GroupeInput,
  GroupeUpdateInput,
  Lieu,
  LoginResponse,
  Parcours,
  ParcoursEtape,
  ParcoursInput,
  ParcoursUpdateInput,
  ParcoursWithEtapes,
  Profile,
  Question,
  QuestionFilters,
  QuestionInput,
  Session,
  SessionDashboard,
  SessionInput,
  SessionUpdateInput,
  SessionWithGroupes,
} from "./types";

const API_URL = (import.meta.env.VITE_API_URL as string) || "http://localhost:8787";
const TOKEN_KEY = "orientation-admin-token";
const REFRESH_KEY = "orientation-admin-refresh";

// ---------- Token storage ----------
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}
export function getRefresh(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}
export function setRefresh(token: string): void {
  localStorage.setItem(REFRESH_KEY, token);
}
export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

// ---------- Erreur API typée ----------
export class ApiCallError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

// ---------- fetch wrapper ----------
async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(API_URL + path, { ...init, headers });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};

  if (res.status === 401) {
    clearTokens();
    if (location.pathname !== "/login") location.href = "/login";
    throw new ApiCallError(401, data.error ?? "unauthorized", data.message ?? "Session expirée");
  }

  if (!res.ok || data.ok === false) {
    throw new ApiCallError(res.status, data.error ?? "error", data.message ?? `HTTP ${res.status}`);
  }
  return data as T;
}

// ---------- Auth ----------
export async function login(email: string, password: string): Promise<Profile> {
  const data = await call<LoginResponse>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  setToken(data.access_token);
  setRefresh(data.refresh_token);
  return data.user;
}

export async function register(email: string, password: string, full_name?: string): Promise<Profile> {
  const data = await call<LoginResponse>("/api/auth/register", {
    method: "POST",
    body: JSON.stringify({ email, password, full_name }),
  });
  setToken(data.access_token);
  setRefresh(data.refresh_token);
  return data.user;
}

export async function logout(): Promise<void> {
  try {
    await call<{ ok: true }>("/api/auth/logout", { method: "POST" });
  } finally {
    clearTokens();
  }
}

export async function fetchMe(): Promise<Profile> {
  const data = await call<{ ok: true; user: Profile }>("/api/auth/me");
  return data.user;
}

// ---------- Lieux ----------
export async function listLieux(): Promise<Lieu[]> {
  const data = await call<{ ok: true; lieux: Lieu[] }>("/api/lieux");
  return data.lieux;
}

export async function getLieu(id: string): Promise<Lieu> {
  const data = await call<{ ok: true; lieu: Lieu }>(`/api/lieux/${id}`);
  return data.lieu;
}

// ---------- Balises ----------
export async function listBalises(lieuId: string): Promise<Balise[]> {
  const data = await call<{ ok: true; balises: Balise[] }>(`/api/lieux/${lieuId}/balises`);
  return data.balises;
}

export async function createBalise(
  lieuId: string,
  input: {
    position_x: number;
    position_y: number;
    nom?: string | null;
    lat?: number | null;
    lng?: number | null;
  }
): Promise<Balise> {
  const data = await call<{ ok: true; balise: Balise }>(`/api/lieux/${lieuId}/balises`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.balise;
}

export async function updateBalise(
  id: string,
  patch: {
    position_x?: number;
    position_y?: number;
    nom?: string | null;
    lat?: number | null;
    lng?: number | null;
    emoji_3d?: string | null;
    couleur_3d?: string | null;
    model_3d_key?: string | null;
  }
): Promise<Balise> {
  const data = await call<{ ok: true; balise: Balise }>(`/api/balises/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  return data.balise;
}

export async function deleteBalise(id: string): Promise<void> {
  await call<void>(`/api/balises/${id}`, { method: "DELETE" });
}

// ---------- Questions ----------
export async function listQuestions(filters: QuestionFilters = {}): Promise<{ total: number; questions: Question[] }> {
  const qs = new URLSearchParams();
  if (filters.niveau) qs.set("niveau", filters.niveau);
  if (filters.matiere) qs.set("matiere", filters.matiere);
  if (filters.search) qs.set("search", filters.search);
  if (filters.limit != null) qs.set("limit", String(filters.limit));
  if (filters.offset != null) qs.set("offset", String(filters.offset));
  const data = await call<{ ok: true; total: number; questions: Question[] }>(
    `/api/questions${qs.toString() ? "?" + qs.toString() : ""}`
  );
  return { total: data.total, questions: data.questions };
}

export async function createQuestion(input: QuestionInput): Promise<Question> {
  const data = await call<{ ok: true; question: Question }>("/api/questions", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.question;
}

export async function updateQuestion(id: string, patch: Partial<QuestionInput>): Promise<Question> {
  const data = await call<{ ok: true; question: Question }>(`/api/questions/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  return data.question;
}

export async function deleteQuestion(id: string): Promise<void> {
  await call<void>(`/api/questions/${id}`, { method: "DELETE" });
}

export async function importQuestions(questions: QuestionInput[]): Promise<{ inserted: number }> {
  const data = await call<{ ok: true; inserted: number }>("/api/questions/import", {
    method: "POST",
    body: JSON.stringify({ questions }),
  });
  return { inserted: data.inserted };
}

// ---------- Parcours ----------
export async function listParcours(lieuId?: string): Promise<Parcours[]> {
  const path = lieuId ? `/api/parcours?lieu_id=${encodeURIComponent(lieuId)}` : "/api/parcours";
  const data = await call<{ ok: true; parcours: Parcours[] }>(path);
  return data.parcours;
}

export async function getParcoursWithEtapes(id: string): Promise<ParcoursWithEtapes> {
  const data = await call<{ ok: true } & ParcoursWithEtapes>(`/api/parcours/${id}`);
  return { parcours: data.parcours, etapes: data.etapes };
}

export async function createParcours(input: ParcoursInput): Promise<Parcours> {
  const data = await call<{ ok: true; parcours: Parcours }>("/api/parcours", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.parcours;
}

export async function updateParcours(id: string, patch: ParcoursUpdateInput): Promise<Parcours> {
  const data = await call<{ ok: true; parcours: Parcours }>(`/api/parcours/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  return data.parcours;
}

export async function deleteParcours(id: string, opts: { force?: boolean } = {}): Promise<void> {
  const qs = opts.force ? "?force=true" : "";
  await call<void>(`/api/parcours/${id}${qs}`, { method: "DELETE" });
}

export async function upsertEtape(
  parcoursId: string,
  baliseId: string,
  questionIds: string[]
): Promise<ParcoursEtape> {
  const data = await call<{ ok: true; etape: ParcoursEtape }>(
    `/api/parcours/${parcoursId}/etapes/${baliseId}`,
    { method: "PUT", body: JSON.stringify({ question_ids: questionIds }) }
  );
  return data.etape;
}

export async function deleteEtape(parcoursId: string, baliseId: string): Promise<void> {
  await call<void>(`/api/parcours/${parcoursId}/etapes/${baliseId}`, {
    method: "DELETE",
  });
}

// ---------- Sessions ----------
export async function listSessions(lieuId?: string): Promise<Session[]> {
  const path = lieuId ? `/api/sessions?lieu_id=${encodeURIComponent(lieuId)}` : "/api/sessions";
  const data = await call<{ ok: true; sessions: Session[] }>(path);
  return data.sessions;
}

export async function getSessionWithGroupes(id: string): Promise<SessionWithGroupes> {
  const data = await call<{ ok: true } & SessionWithGroupes>(`/api/sessions/${id}`);
  return { session: data.session, groupes: data.groupes };
}

export async function createSession(input: SessionInput): Promise<Session> {
  const data = await call<{ ok: true; session: Session }>("/api/sessions", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.session;
}

export async function updateSession(id: string, patch: SessionUpdateInput): Promise<Session> {
  const data = await call<{ ok: true; session: Session }>(`/api/sessions/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  return data.session;
}

export async function deleteSession(id: string): Promise<void> {
  await call<void>(`/api/sessions/${id}`, { method: "DELETE" });
}

export async function startSession(id: string): Promise<Session> {
  const data = await call<{ ok: true; session: Session }>(`/api/sessions/${id}/start`, {
    method: "POST",
  });
  return data.session;
}

export async function endSession(id: string): Promise<Session> {
  const data = await call<{ ok: true; session: Session }>(`/api/sessions/${id}/end`, {
    method: "POST",
  });
  return data.session;
}

export async function createGroupe(sessionId: string, input: GroupeInput): Promise<Groupe> {
  const data = await call<{ ok: true; groupe: Groupe }>(`/api/sessions/${sessionId}/groupes`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.groupe;
}

export async function updateGroupe(id: string, patch: GroupeUpdateInput): Promise<Groupe> {
  const data = await call<{ ok: true; groupe: Groupe }>(`/api/groupes/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  return data.groupe;
}

export async function deleteGroupe(id: string): Promise<void> {
  await call<void>(`/api/groupes/${id}`, { method: "DELETE" });
}

export async function resetGroupe(id: string): Promise<void> {
  await call<{ ok: true }>(`/api/groupes/${id}/reset`, { method: "POST" });
}

export async function listAllParcours(): Promise<Parcours[]> {
  return listParcours();
}

// ---------- Dashboard ----------
export async function fetchSessionDashboard(sessionId: string): Promise<SessionDashboard> {
  const data = await call<{ ok: true } & SessionDashboard>(`/api/sessions/${sessionId}/dashboard`);
  // strip "ok" to keep type clean
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { ok: _ok, ...rest } = data as SessionDashboard & { ok: true };
  return rest as SessionDashboard;
}

export async function adminUnblock(
  sessionId: string,
  groupeId: string,
  baliseId: string
): Promise<void> {
  await call<{ ok: true }>(
    `/api/sessions/${sessionId}/groupes/${groupeId}/unblock/${baliseId}`,
    { method: "POST", body: JSON.stringify({}) }
  );
}
